
import { defineComponent, onMounted, reactive, ref, toRefs, unref } from 'vue'
import { ElForm, ElMessage } from 'element-plus'
import { Country } from '@/data/country'
import axios from 'axios'
import networkConfig from '@/config/default/net.config'
import * as Sentry from '@sentry/vue'
import { useRouter } from 'vue-router'

/** cheryl.jiang
 * 1. 本页面是主页面，链接必须包含jobTitle、jobId、type 3个字段页面才是有效的，有referrer字段就显示表单KN Referral Email，否则就隐藏。
 * 2. 表单数据全部存储在data变量@param mokaForm{} 里面。
 * 3. 初始化页面 @method onMounted()，@param search 获取页面链接，@param schemas 定义4个链接字段变量，通过@method new Map() 获取到具体的值，
 *    将值分别赋给jobTitle，mokaForm.jobId，mokaForm.type，isDisabledReferrer。
 * 4. 整个页面表单除了Address Line2 和 Zip Code是非必填，其他都是必填项。
 * 5. @method
 *  5.1 getFile(): 控制文件大小，最大不能超过5MB，最小大于1kb，文件只能上传一个，重复上传会覆盖之前的文件；
 *  5.2 changeCountryCode(): 联动国家和电话号码的区号；
 *  5.3 submitForm(): 上传表单数据，一共15个字段，有值无值都要上传，上传成功后跳转到Success页面。
 */

export default defineComponent({
  setup() {
    const mokaFormRef = ref(ElForm)
    const router = useRouter()
    const dataMap = reactive({
      publicPath: process.env.VUE_APP_PUBLIC_PATH,
      mokaForm: {
        // mokaJobId: '3dc30205-ce07-4038-bbaa-b40fd3f3f3db',
        mokaJobId: '',
        // isRecommendation: 0,
        firstName: '',
        lastName: '',
        email: '',
        referrer: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        province: '',
        country: 'China',
        zipCode: '',
        countryCode: 'CN',
        phone: '',
        resume: '',
        type: ''
      },
      countryValue: Country,
      searchable: false,
      agreement: false,
      isDisabledReferrer: false,
      jobTitle: '--',
      rules: {
        firstName: [
          { required: true, message: 'Please input first name', trigger: 'blur' }
        ],
        lastName: [
          { required: true, message: 'Please input last name', trigger: 'blur' }
        ],
        email: [
          { required: true, message: 'Please input email', trigger: 'blur' },
          { type: 'email', message: 'Please input correct email address', trigger: 'blur' }
        ],
        referrer: [
          { required: false, message: 'Please input referrer', trigger: 'blur' }
        ],
        addressLine1: [
          { required: true, message: 'Please input address line1', trigger: 'blur' },
          { min: 1, max: 10000, message: 'Length should be 3 to 5', trigger: 'blur' }
        ],
        city: [
          { required: true, message: 'Please input city', trigger: 'blur' }
        ],
        province: [
          { required: true, message: 'Please input province', trigger: 'blur' }
        ],
        country: [
          { required: true, message: 'Please select country', trigger: 'change' }
        ],
        countryCode: [
          { required: true, message: 'Please select country code', trigger: 'change' }
        ],
        phone: [
          { required: true, message: 'Please input phone', trigger: 'blur' }
        ]
      },
      loading: false,
      submitError: '',
      showExceedText: false,
      exceedText: '',
      fileList: [] as any
    })
    const methods = reactive({
      submitForm() {
        const form = unref(mokaFormRef)
        form.validate(async(valid: Boolean) => {
          if (valid) {
            if (!dataMap.mokaForm.resume) {
              ElMessage.error('Please upload your resume.')
              return false
            }
            // if (!dataMap.searchable) {
            //   ElMessage.error('Please check Privacy Statement')
            //   return false
            // }
            if (!dataMap.agreement) {
              ElMessage.error('Please check Kuehne+Nagel Data Privacy Statement')
              return false
            }
            // dataMap.mokaForm.isRecommendation = dataMap.mokaForm.referrer !== '' ? 1 : 0
            dataMap.mokaForm.mokaJobId = dataMap.mokaForm.mokaJobId !== '' ? dataMap.mokaForm.mokaJobId : '3dc30205-ce07-4038-bbaa-b40fd3f3f3db'
            dataMap.loading = true
            const dataObj = new FormData()
            dataObj.append('mokaJobId', dataMap.mokaForm.mokaJobId)
            // dataObj.append('isRecommendation', `${dataMap.mokaForm.isRecommendation}`)
            dataObj.append('firstName', dataMap.mokaForm.firstName)
            dataObj.append('lastName', dataMap.mokaForm.lastName)
            dataObj.append('email', dataMap.mokaForm.email)
            dataObj.append('referrer', dataMap.mokaForm.referrer)
            dataObj.append('addressLine1', dataMap.mokaForm.addressLine1)
            dataObj.append('addressLine2', dataMap.mokaForm.addressLine2)
            dataObj.append('province', dataMap.mokaForm.province)
            dataObj.append('city', dataMap.mokaForm.city)
            dataObj.append('country', dataMap.mokaForm.country)
            dataObj.append('zipCode', dataMap.mokaForm.zipCode)
            dataObj.append('countryCode', dataMap.mokaForm.countryCode)
            dataObj.append('phone', dataMap.mokaForm.phone)
            dataObj.append('resume', dataMap.mokaForm.resume)
            dataObj.append('type', dataMap.mokaForm.type)
            axios.post(networkConfig.host + '/moka/job/application', dataObj).then((res) => {
              if (res.data.code === 200) {
                ElMessage.success('Successful!')
                dataMap.loading = false
                dataMap.submitError = ''
                router.push({
                  name: 'Success'
                })
              } else {
                ElMessage.error(res.data.msg)
                dataMap.loading = false
                dataMap.submitError = 'Error: ' + res.data.msg
              }
            }).catch((err) => {
              Sentry.captureException(err)
              dataMap.loading = false
              ElMessage.error(err)
              dataMap.submitError = 'Error: ' + err
            })
          }
        })
      },
      getFile(value: any) {
        const sizeKB = value.size / 1024 // 换算成kb
        const size = value.size / 1024 / 1024 // 换算成MB
        if (size > 5) {
          dataMap.mokaForm.resume = ''
          ElMessage.error('The file size is greater than 5MB, please upload again.')
          dataMap.showExceedText = true
          dataMap.exceedText = 'The file size is greater than 5MB, please upload again.'
        } else if (sizeKB < 1) {
          dataMap.mokaForm.resume = ''
          ElMessage.error('The file size is less than 1kb, please upload again.')
          dataMap.showExceedText = true
          dataMap.exceedText = 'The file size is less than 1kb, please upload again.'
        } else {
          dataMap.mokaForm.resume = value.raw
          dataMap.showExceedText = false
        }
        dataMap.fileList.shift(dataMap.mokaForm.resume)
        dataMap.fileList.push(dataMap.mokaForm.resume)
      },
      changeCountryCode(val: any) {
        dataMap.mokaForm.countryCode = '+' + val
      }
    })
    onMounted(() => {
      const search = new URLSearchParams(location.search)
      const schemas = [
        {
          field: 'jobId',
          required: true,
          defaultValue: null
        },
        {
          field: 'jobTitle',
          required: true,
          defaultValue: null
        },
        {
          field: 'type',
          required: true,
          defaultValue: null
        },
        {
          field: 'referrer',
          required: false,
          defaultValue: ''
        }
      ]

      const params = new Map()

      for (const schema of schemas) {
        const { field, required, defaultValue } = schema
        let fieldValue = search.get(field) || defaultValue

        if (required && fieldValue === defaultValue) {
          ElMessage.error({ message: `Link missing necessary parameters [ ${field} ]`, duration: 30 * 1000 })
        }

        if (typeof fieldValue === 'string') {
          fieldValue = decodeURIComponent(fieldValue).trim()
        }
        params.set(field, fieldValue)
      }
      dataMap.jobTitle = params.get('jobTitle')
      dataMap.mokaForm.mokaJobId = params.get('jobId')
      dataMap.mokaForm.referrer = params.get('referrer')
      dataMap.mokaForm.type = params.get('type')
      dataMap.isDisabledReferrer = dataMap.mokaForm.referrer !== ''
    })
    return {
      mokaFormRef,
      ...toRefs(dataMap),
      ...toRefs(methods)
    }
  }
})
